import React from "react";

import { getDebugLogs, clearDebugLogs, subscribeDebugLogs, isPanelVisible, DebugLogEntry } from "@utils";

/*
 * 検証用デバッグパネル（dev環境限定）
 *
 * 右下のボタンをタップするとログ一覧を開く。iPhone で開発者ツールが使えないため、
 * 画面上でエラーと計測値を確認できるようにしている。
 *
 * ※ styled-components を使わず、すべてインラインスタイルで書いている。
 *    調査対象が「スタイル適用・描画の不具合」なので、パネル自体が同じ仕組みに
 *    影響されないようにするため。
 */

const levelColor: Record<string, string> = {
  error: "#ff4c25",
  warn: "#e5a100",
  info: "#3a7afe",
  measure: "#00875a",
};

export const DebugPanel = () => {
  const [open, setOpen] = React.useState(false);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    if (!isPanelVisible()) return;
    return subscribeDebugLogs(() => forceUpdate());
  }, []);

  if (!isPanelVisible()) return null;

  const logs: DebugLogEntry[] = getDebugLogs();
  const errorCount = logs.filter((l) => l.level === "error").length;

  const asText = () => logs.map((l) => `${l.time} [${l.level}] ${l.tag} ${l.message}`).join("\n");

  const copy = async () => {
    const text = asText();
    try {
      await navigator.clipboard.writeText(text);
      alert("ログをコピーしました");
    } catch (e) {
      // クリップボードが使えない場合は選択してもらう
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        alert("ログをコピーしました");
      } catch (e2) {
        alert("コピーできませんでした。画面を長押しして選択してください。");
      }
      document.body.removeChild(ta);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed",
          right: 12,
          bottom: 12,
          zIndex: 2147483647,
          width: 56,
          height: 56,
          borderRadius: 28,
          border: "none",
          backgroundColor: errorCount > 0 ? "#ff4c25" : "#333",
          color: "#fff",
          fontSize: 12,
          fontWeight: "bold",
          boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
        }}
      >
        LOG
        <br />
        {logs.length}
        {errorCount > 0 ? `/${errorCount}!` : ""}
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483646,
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", gap: 8, padding: 8, borderBottom: "1px solid #ddd", flexShrink: 0 }}>
            <button onClick={() => setOpen(false)} style={btn}>閉じる</button>
            <button onClick={copy} style={btn}>コピー</button>
            <button onClick={() => clearDebugLogs()} style={btn}>クリア</button>
            <span style={{ alignSelf: "center", fontSize: 12 }}>{logs.length}件（error {errorCount}）</span>
          </div>
          <div style={{ flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch", padding: 8 }}>
            {logs.length === 0 && <p style={{ fontSize: 13 }}>ログはまだありません</p>}
            {logs
              .slice()
              .reverse()
              .map((l, i) => (
                <div key={i} style={{ borderBottom: "1px solid #eee", padding: "6px 0", fontSize: 11, lineHeight: 1.5 }}>
                  <span style={{ color: "#888" }}>{l.time}</span>{" "}
                  <span style={{ color: levelColor[l.level] || "#333", fontWeight: "bold" }}>[{l.level}]</span>{" "}
                  <span style={{ fontWeight: "bold" }}>{l.tag}</span>
                  <div style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}>{l.message}</div>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

const btn: React.CSSProperties = {
  padding: "8px 12px",
  fontSize: 13,
  borderRadius: 4,
  border: "1px solid #ccc",
  backgroundColor: "#f5f5f5",
};
